import React, { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/core/macro';
import { useReducedMotion } from 'motion/react';
import { Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TimingPreviewPause, TimingPreviewPlay, TimingPreviewRestart } from './timingPreviewIcons';
import { Block } from '../classes/Block';
import { VideoSettings, VideoSettingsPanel, calculateVideoDimensions } from './VideoSettings';
import { useProject } from '../contexts/ProjectContext';
import { authFetch } from '../utils/authFetch';
import { getApiUrl } from '../utils/api';
import { toFontUrlMap } from '../utils/fontHelpers';
import { RenderPayloadColumnDef, transformBlockToApiFormat } from '../utils/renderPayload';
import './InstantRenderDialog.css';

export function getInstantRenderTiming(contentHeight: number, viewportHeight: number, seconds: number): { distance: number; duration: number } {
  return { distance: Math.max(0, contentHeight - viewportHeight), duration: Math.trunc(seconds) * 1000 };
}

function formatPreviewTime(seconds: number): string {
  const wholeSeconds = Math.max(0, Math.floor(seconds));
  return `${Math.floor(wholeSeconds / 60).toString().padStart(2, '0')}:${(wholeSeconds % 60).toString().padStart(2, '0')}`;
}

const TIMELINE_RESUME_DELAY_MS = 200;

interface InstantRenderDialogProps {
  open: boolean;
  onClose: () => void;
  blocks: Block[];
  columnDefs: RenderPayloadColumnDef[];
  settings: VideoSettings;
  onDurationChange?: (seconds: number) => void | Promise<void>;
}

export default function InstantRenderDialog({ open, onClose, blocks, columnDefs, settings, onDurationChange }: InstantRenderDialogProps) {
  const reducedMotion = useReducedMotion();
  const { selectedProject } = useProject();
  const [html, setHtml] = useState('');
  const [status, setStatus] = useState<'loading' | 'ready' | 'playing' | 'finished' | 'error' | 'empty'>('loading');
  const [retry, setRetry] = useState(0);
  const [scale, setScale] = useState(0);
  const [draftDuration, setDraftDuration] = useState(settings.video_time);
  const [position, setPosition] = useState(0);
  const viewportRef = useRef<HTMLDivElement>(null);
  const animationRef = useRef<Animation | null>(null);
  const seekResumeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const seekWasPlayingRef = useRef(false);
  const generationRef = useRef(0);
  const dimensions = calculateVideoDimensions(settings);
  const duration = Math.trunc(settings.video_time);
  const validTiming = Number.isFinite(duration) && duration > 0;

  useEffect(() => {
    if (open) setDraftDuration(settings.video_time);
  }, [open, settings.video_time]);

  useEffect(() => {
    if (!open) return;
    const generationCounter = generationRef;
    const generation = ++generationCounter.current;
    const controller = new AbortController();
    setHtml('');
    setStatus('loading');
    setPosition(0);
    const visibleBlocks = blocks.filter(block => !block.isHidden);
    if (!visibleBlocks.length || !columnDefs.length) {
      setStatus('empty');
    } else if (!validTiming || !selectedProject?.id) {
      setStatus('error');
    } else {
      void (async () => {
        try {
          const response = await authFetch(`${getApiUrl()}/html-preview?projectId=${encodeURIComponent(selectedProject.id)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            signal: controller.signal,
            body: JSON.stringify({
              instant_render: true,
              video_settings: { ...settings, fonts: JSON.stringify(toFontUrlMap(settings.fonts)), logos: JSON.stringify(settings.logos) },
              data: visibleBlocks.map(block => transformBlockToApiFormat(block, { columnDefs, propertiesMode: 'render' })),
            }),
          });
          if (!response.ok) throw new Error('Preview request failed');
          const result = await response.json();
          if (result.status !== 'OK' || !result.html) throw new Error('Preview unavailable');
          if (!controller.signal.aborted && generation === generationCounter.current) setHtml(result.html);
        } catch (error) {
          if (!controller.signal.aborted) console.error('Instant Render request failed:', error);
          if (!controller.signal.aborted && generation === generationCounter.current) setStatus('error');
        }
      })();
    }
    return () => {
      controller.abort();
      ++generationCounter.current;
      if (seekResumeTimeoutRef.current !== null) clearTimeout(seekResumeTimeoutRef.current);
      seekResumeTimeoutRef.current = null;
      seekWasPlayingRef.current = false;
      animationRef.current?.cancel();
      animationRef.current = null;
    };
  }, [open, blocks, columnDefs, settings, selectedProject?.id, retry, validTiming]);

  useEffect(() => {
    const viewport = viewportRef.current;
    if (!open || !viewport) return;
    const observer = new ResizeObserver(([entry]) => {
      setScale(Math.min(entry.contentRect.width / dimensions.width, entry.contentRect.height / dimensions.height));
    });
    observer.observe(viewport);
    return () => observer.disconnect();
  }, [open, dimensions.width, dimensions.height, html]);

  useEffect(() => {
    if (!open || status !== 'playing') return;
    let frame: number;
    const updatePosition = () => {
      const animation = animationRef.current;
      if (!animation) return;
      setPosition(Math.min(duration, Math.max(0, Number(animation.currentTime ?? 0) / 1000)));
      frame = requestAnimationFrame(updatePosition);
    };
    frame = requestAnimationFrame(updatePosition);
    return () => cancelAnimationFrame(frame);
  }, [open, status, duration]);

  const preparePlayback = async (iframe: HTMLIFrameElement) => {
    const generation = generationRef.current;
    const doc = iframe.contentDocument;
    if (!doc) return;
    try {
      await doc.fonts.ready;
      await Promise.all(Array.from(doc.images).map(async image => {
        try {
          await image.decode();
        } catch {
          // The sandbox blocks inline onerror handlers. Apply the renderer's
          // supplied placeholder directly, without executing embedded scripts.
          const fallback = image.dataset.fallbackSrc;
          if (generation !== generationRef.current || !iframe.isConnected) return;
          if (fallback?.startsWith('data:image/png;base64,')) {
            image.src = fallback;
            await image.decode().catch(() => undefined);
          }
        }
      }));
      if (generation !== generationRef.current || !iframe.isConnected) return;
      doc.documentElement.style.overflow = 'hidden';
      doc.documentElement.style.background = settings.transparent ? 'transparent' : settings.background_color;
      const { distance, duration: milliseconds } = getInstantRenderTiming(
        Math.max(doc.documentElement.scrollHeight, doc.body.scrollHeight), dimensions.height, settings.video_time,
      );
      const style = doc.createElement('style');
      style.textContent = `@keyframes fintitles-instant-scroll { from { transform: translateY(0); } to { transform: translateY(-${distance}px); } }
        body { animation: fintitles-instant-scroll ${milliseconds}ms linear both paused; }`;
      doc.head.append(style);
      // Reading animations flushes style and returns the CSS animation, initially paused.
      const animation = doc.body.getAnimations()[0];
      if (!animation) throw new Error('Playback unavailable');
      animationRef.current = animation;
      animation.onfinish = () => {
        if (generation === generationRef.current && Number(animation.currentTime) >= milliseconds) {
          setPosition(duration);
          setStatus('finished');
        }
      };
      setStatus('ready');
    } catch (error) {
      console.error('Instant Render playback initialization failed:', error);
      if (generation === generationRef.current && iframe.isConnected) setStatus('error');
    }
  };

  const ready = status === 'ready' || status === 'playing' || status === 'finished';
  const togglePlayback = () => {
    const animation = animationRef.current;
    if (!animation) return;
    if (seekResumeTimeoutRef.current !== null) clearTimeout(seekResumeTimeoutRef.current);
    seekResumeTimeoutRef.current = null;
    seekWasPlayingRef.current = false;
    if (status === 'playing') {
      animation.pause();
      setPosition(Math.min(duration, Math.max(0, Number(animation.currentTime ?? 0) / 1000)));
      setStatus('ready');
    } else {
      animation.play();
      setStatus('playing');
    }
  };

  const seek = (seconds: number) => {
    const animation = animationRef.current;
    if (!animation || !Number.isFinite(seconds)) return;
    const next = Math.min(duration, Math.max(0, seconds));
    if (!seekResumeTimeoutRef.current) seekWasPlayingRef.current = status === 'playing';
    if (seekWasPlayingRef.current) {
      if (status === 'playing') animation.pause();
      setStatus('ready');
    }
    if (seekResumeTimeoutRef.current !== null) clearTimeout(seekResumeTimeoutRef.current);
    seekResumeTimeoutRef.current = null;
    animation.currentTime = next * 1000;
    setPosition(next);
    if (next === duration) {
      animation.pause();
      seekWasPlayingRef.current = false;
      setStatus('finished');
    } else if (status === 'finished' || seekWasPlayingRef.current) {
      setStatus('ready');
      if (seekWasPlayingRef.current) {
        seekResumeTimeoutRef.current = setTimeout(() => {
          seekResumeTimeoutRef.current = null;
          if (animationRef.current === animation) {
            animation.play();
            setStatus('playing');
          }
          seekWasPlayingRef.current = false;
        }, TIMELINE_RESUME_DELAY_MS);
      }
    }
  };

  return (
    <Dialog open={open} onClose={onClose} transitionDuration={reducedMotion ? 0 : undefined} maxWidth="lg" fullWidth aria-labelledby="instant-render-title" PaperProps={{ className: 'instant-render-dialog' }}>
      <DialogTitle id="timing-preview-heading" className="instant-render-title">
        <span id="instant-render-title">{t`Scrolling Preview`}</span>
        <IconButton className="instant-render-close" onClick={onClose} aria-label={t`Close`}><CloseIcon sx={{ fontSize: 18 }} aria-hidden="true" /></IconButton>
      </DialogTitle>
      <DialogContent>
        {onDurationChange && <form className="instant-render-timing" noValidate onSubmit={event => {
          event.preventDefault();
          if (Number.isFinite(draftDuration) && draftDuration >= 1 && draftDuration !== settings.video_time) {
            void onDurationChange(draftDuration);
          }
        }}>
          <VideoSettingsPanel settings={{ ...settings, video_time: draftDuration }}
            onSettingsChange={next => setDraftDuration(next.video_time)} showTitle={false} timingOnly />
          <Button type="submit" disabled={!Number.isFinite(draftDuration) || draftDuration < 1 || draftDuration === settings.video_time}>{t`Apply`}</Button>
        </form>}
        <div ref={viewportRef} className="instant-render-viewport">
          {html && <iframe title={t`Scrolling Preview`} sandbox="allow-same-origin" srcDoc={html}
            onLoad={event => { void preparePlayback(event.currentTarget); }}
            style={{ width: dimensions.width, height: dimensions.height, transform: `scale(${scale})`, visibility: ready ? 'visible' : 'hidden' }} />}
          {status === 'loading' && <div className="instant-render-message" role="status"><CircularProgress size={24} /><span>{t`Loading preview…`}</span></div>}
          {status === 'empty' && <div className="instant-render-message" role="status">{t`No visible credits to preview.`}</div>}
          {status === 'error' && <div className="instant-render-message" role="alert">
            <span>{validTiming ? t`Could not load the preview. Try again.` : t`Enter a duration of at least one second to preview.`}</span>
            {validTiming && <Button onClick={() => setRetry(value => value + 1)}>{t`Retry`}</Button>}
          </div>}
        </div>
        <div className="instant-render-timeline" dir="ltr">
          <input type="range" min={0} max={validTiming ? duration : 1} step={0.01}
            value={position} disabled={!ready} aria-label={t`Playback position`}
            aria-valuetext={`${formatPreviewTime(position)} / ${formatPreviewTime(validTiming ? duration : 0)}`}
            style={{ '--preview-progress': `${validTiming ? position / duration * 100 : 0}%` } as React.CSSProperties}
            onChange={event => seek(Number(event.target.value))} />
          <span className="instant-render-time" aria-hidden="true">{formatPreviewTime(position)} / {formatPreviewTime(validTiming ? duration : 0)}</span>
        </div>
      </DialogContent>
      <DialogActions className="instant-render-controls">
        <div className="instant-render-transport" role="group" aria-label={t`Playback controls`}>
        <Button className="instant-render-play" disableElevation disableRipple onClick={togglePlayback} disabled={!ready || status === 'finished'}
          startIcon={status === 'playing' ? <TimingPreviewPause /> : <TimingPreviewPlay />}>
          {status === 'playing' ? t`Pause` : t`Play`}
        </Button>
        <Button className="instant-render-restart" disableRipple disabled={!ready}
          startIcon={<TimingPreviewRestart />}
          onClick={() => {
            const animation: Animation | null = animationRef.current;
            if (!animation) return;
            if (seekResumeTimeoutRef.current !== null) clearTimeout(seekResumeTimeoutRef.current);
            seekResumeTimeoutRef.current = null;
            seekWasPlayingRef.current = false;
            animation.pause();
            animation.currentTime = 0;
            setPosition(0);
            setStatus('ready');
          }}>{t`Restart`}</Button>
        </div>
        <span role="status">{status === 'finished' ? t`Finished` : ready ? t`${duration} seconds` : ''}</span>
      </DialogActions>
    </Dialog>
  );
}
